import { createBridgeIndexingApi } from './features/indexing/bridgeIndexingApi';
import { createLegacyIndexingApi } from './features/indexing/legacyIndexingApi';
import { createLegacySynonymsApi } from './features/synonyms/legacySynonymsApi';
import { createBridgeSynonymsApi } from './features/synonyms/bridgeSynonymsApi';
import { createLegacyFieldsApi } from './features/fields/legacyFieldsApi';
import { createBridgeFieldsApi } from './features/fields/bridgeFieldsApi';
import { createLegacyFiltersApi } from './features/filters/legacyFiltersApi';
import { createBridgeFiltersApi } from './features/filters/bridgeFiltersApi';
import { createBridgeFacetValuesApi } from './features/filters/bridgeFacetValuesApi';
import { createBridgeAnalyticsApi } from './features/analytics/bridgeAnalyticsApi';
import { createLegacyAnalyticsApi } from './features/analytics/legacyAnalyticsApi';
import { createBridgeEmptyQueryApi } from './features/emptyQuery/bridgeEmptyQueryApi';
import { createBridgeProductSelectorApi } from './features/productSelector/bridgeProductSelectorApi';
import { createLegacyEmptyQueryApi } from './features/emptyQuery/legacyEmptyQueryApi';
import { createLegacyProductSelectorApi } from './features/productSelector/legacyProductSelectorApi';
import { createBridgeCatalogSelectorApi } from './features/catalogSelector/bridgeCatalogSelectorApi';
import { createLegacyCatalogSelectorApi } from './features/catalogSelector/legacyCatalogSelectorApi';
import { createBridgePayloadProviderApi } from './features/payloadProvider/bridgePayloadProviderApi';
import { createBridgeVariantPresentationApi } from './features/variantPresentation/bridgeVariantPresentationApi';
import { createLegacyProductPresentationApis } from './features/productPresentation/legacyProductPresentationApi';
import { createBridgeFacetPolicyApi } from './features/facets/bridgeFacetPolicyApi';
import { createLegacyFacetPolicyApi } from './features/facets/legacyFacetPolicyApi';
import { pluginAppInIframe } from '../util/appUtils';
import { createBridgeMerchandisingApi } from './features/merchandising/bridgeMerchandisingApi';
import { createLegacyMerchandisingApi } from './features/merchandising/legacyMerchandisingApi';
import { createLegacyTaxonomyApi } from './features/taxonomy/legacyTaxonomyApi';
import { createLegacySynonymSuggestionsApi } from './features/synonyms/legacySynonymSuggestionsApi';
import { createLegacySuggestionOverridesApi } from './features/suggestions/legacySuggestionOverridesApi';

const TRANSPORTS = {
  legacy: {
    synonymsApi: createLegacySynonymsApi,
    fieldsApi: createLegacyFieldsApi,
    filtersApi: createLegacyFiltersApi,
    facetValuesApi: null, // No legacy transport — bridge-only in v1
    facetPolicyApi: createLegacyFacetPolicyApi,
    analyticsApi: createLegacyAnalyticsApi,
    emptyQueryApi: createLegacyEmptyQueryApi,
    productSelectorApi: createLegacyProductSelectorApi,
    indexingApi: createLegacyIndexingApi,
    catalogSelectorApi: createLegacyCatalogSelectorApi,
    productPresentationApis: createLegacyProductPresentationApis,
    payloadProviderApi: null,
    variantPresentationApi: null,
    merchandisingApi: createLegacyMerchandisingApi,
    taxonomyApi: createLegacyTaxonomyApi,
    synonymSuggestionsApi: createLegacySynonymSuggestionsApi,
    suggestionOverridesApi: createLegacySuggestionOverridesApi,
  },
  bridge: {
    synonymsApi: createBridgeSynonymsApi,
    fieldsApi: createBridgeFieldsApi,
    filtersApi: createBridgeFiltersApi,
    facetValuesApi: createBridgeFacetValuesApi,
    facetPolicyApi: createBridgeFacetPolicyApi,
    analyticsApi: createBridgeAnalyticsApi,
    emptyQueryApi: createBridgeEmptyQueryApi,
    productSelectorApi: createBridgeProductSelectorApi,
    indexingApi: createBridgeIndexingApi,
    catalogSelectorApi: createBridgeCatalogSelectorApi,
    productPresentationApis: null,
    payloadProviderApi: createBridgePayloadProviderApi,
    variantPresentationApi: createBridgeVariantPresentationApi,
    merchandisingApi: createBridgeMerchandisingApi,
    taxonomyApi: null,
    synonymSuggestionsApi: null,
    suggestionOverridesApi: null,
  },
};

// ---------------------------------------------------------------------------
// Initial transport resolution
// ---------------------------------------------------------------------------

/**
 * resolveInitialTransport — determines the correct transport for store init.
 *
 * Rules:
 *   - Non-iframe context  → 'legacy'  (cloud admin, standalone)
 *   - Iframe context      → 'bridge'  (embedded production shell)
 *
 * Exported for unit testing; store/index.js is the only production caller.
 */
export function resolveInitialTransport() {
  if (!pluginAppInIframe()) return 'legacy';
  return 'bridge';
}

/**
 * Creates the service registry that gets injected into redux-thunk extraArgument.
 * Feature thunks access their API through: thunkAPI.extra.serviceRegistry.synonymsApi
 *
 * Transport selection happens here, not in feature code.
 * Call registry.setTransport(name) to switch at runtime.
 *
 * @param {object}  [options]
 * @param {string}  [options.transport='legacy']      Initial transport name.
 * @param {boolean} [options.embeddedBridge=false]    When true, calling setTransport('legacy')
 *                                                    throws in dev/test — fail-fast guard for
 *                                                    the embedded production path.
 */
export function createServiceRegistry({ transport = 'legacy', embeddedBridge = false } = {}) {
  const registry = {
    _embeddedBridge: embeddedBridge,
    synonymsApi: null,
    fieldsApi: null,
    filtersApi: null,
    facetValuesApi: null,
    facetPolicyApi: null,
    analyticsApi: null,
    emptyQueryApi: null,
    productSelectorApi: null,
    indexingApi: null,
    catalogSelectorApi: null,
    payloadProviderApi: null,
    variantPresentationApi: null,
    merchandisingApi: null,
    taxonomyApi: null,
    synonymSuggestionsApi: null,
    suggestionOverridesApi: null,

    setTransport(name) {
      // Fail-fast guard: legacy transport must not be used on the embedded production
      // shell path.  Only enforced in dev/test to surface accidental regressions early;
      // production builds skip the throw so a silent failure is always preferred over
      // a crash reaching the operator.
      if (this._embeddedBridge && name === 'legacy' && process.env.NODE_ENV !== 'production') {
        throw new Error(
          '[serviceRegistry] Legacy transport is forbidden on the embedded production path. '
          + 'Use bridge transport for embedded shell.',
        );
      }
      const factories = TRANSPORTS[name];
      if (!factories) {
        throw new Error(`Unknown transport: "${name}". Available: ${Object.keys(TRANSPORTS).join(', ')}`);
      }
      registry.synonymsApi = factories.synonymsApi();
      registry.fieldsApi = factories.fieldsApi();
      registry.filtersApi = factories.filtersApi();
      registry.facetValuesApi = factories.facetValuesApi ? factories.facetValuesApi() : null;
      registry.facetPolicyApi = factories.facetPolicyApi ? factories.facetPolicyApi() : null;
      registry.analyticsApi = factories.analyticsApi ? factories.analyticsApi() : null;
      registry.emptyQueryApi = factories.emptyQueryApi ? factories.emptyQueryApi() : null;
      registry.productSelectorApi = factories.productSelectorApi ? factories.productSelectorApi() : null;
      registry.indexingApi = factories.indexingApi ? factories.indexingApi() : null;
      registry.catalogSelectorApi = factories.catalogSelectorApi ? factories.catalogSelectorApi() : null;
      const productPresentationApis = factories.productPresentationApis
        ? factories.productPresentationApis()
        : null;
      registry.payloadProviderApi = productPresentationApis?.payloadProviderApi
        || (factories.payloadProviderApi ? factories.payloadProviderApi() : null);
      registry.variantPresentationApi = productPresentationApis?.variantPresentationApi
        || (factories.variantPresentationApi ? factories.variantPresentationApi() : null);
      registry.merchandisingApi = factories.merchandisingApi
        ? factories.merchandisingApi()
        : null;
      registry.taxonomyApi = factories.taxonomyApi ? factories.taxonomyApi() : null;
      registry.synonymSuggestionsApi = factories.synonymSuggestionsApi
        ? factories.synonymSuggestionsApi()
        : null;
      registry.suggestionOverridesApi = factories.suggestionOverridesApi
        ? factories.suggestionOverridesApi()
        : null;
    },
  };

  registry.setTransport(transport);
  return registry;
}
